import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { flushSync } from 'react-dom';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { 
  Upload, 
  ArrowLeft, 
  ArrowRight,
  LinkIcon, 
  CheckCircle, 
  AlertTriangle 
} from "lucide-react";
import { useToast } from "../../../components/ui/use-toast";
import FileUploader from "../../../components/CSVImport/FileUploader";
import ImportTemplate from "../../../components/CSVImport/ImportTemplate";
import ColumnMapper from "../../../components/CSVImport/ColumnMapper";
import DuplicateChecker from "../../../components/CSVImport/DuplicateChecker";
import ImportSummary from "../../../components/CSVImport/ImportSummary";
import ImportNameInput from "../../../components/CSVImport/ImportNameInput";
import LoadingOverlay from "../../../components/CSVImport/LoadingOverlay";
import { importApi } from "../../../api/importApi";

// Fonction pour parser le CSV
const parseCSV = (content: string): string[][] => {
  // Implémentation simple d'un parser CSV
  const rows = content.split("\n").filter(row => row.trim());
  return rows.map(row => row.split(";").map(cell => cell.trim()));
};

// Fonction pour vérifier si une ligne est vide
const isEmptyRow = (row: string[]): boolean => {
  return row.every(cell => cell.trim() === '');
};

// Fonction pour générer un nom d'import par défaut
const generateDefaultImportName = (): string => {
  const now = new Date();
  return `Import du ${now.toLocaleDateString('fr-FR')} à ${now.toLocaleTimeString('fr-FR')}`;
};

const CSVImportPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [emptyRowsCount, setEmptyRowsCount] = useState(0);
  const [importName, setImportName] = useState<string>("");
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [missingDialogOpen, setMissingDialogOpen] = useState(false);

  // Synthèse de l'import
  const [importSummary, setImportSummary] = useState({
    totalRecords: 0,
    successCount: 0,
    failureCount: 0,
    duplicateCount: 0,
    emptyRowsCount: 0
  });
  const [importError, setImportError] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Générer un nom d'import par défaut
    if (!importName) {
      setImportName(generateDefaultImportName());
    }
  }, [importName]);

  const handleFileSelect = (file: File, fileName: string) => {
    setSelectedFile(file);
    
    // Lire le contenu du fichier CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        const content = e.target.result as string;
        const parsedData = parseCSV(content);
        
        if (parsedData.length > 0) {
          const headers = parsedData[0];
          setCsvHeaders(headers);
          
          // Filtrer les lignes vides et compter les lignes vides
          const nonEmptyRows = parsedData.slice(1).filter(row => !isEmptyRow(row));
          const emptyRows = parsedData.slice(1).length - nonEmptyRows.length;
          setEmptyRowsCount(emptyRows);
          
          // Mettre à jour le CSV content avec les lignes non vides
          setCsvContent([parsedData[0], ...nonEmptyRows]);
          
          if (emptyRows > 0) {
            toast({
              title: "Lignes vides détectées",
              description: `${emptyRows} ligne(s) vide(s) ne seront pas importées.`,
            });
          }
        }
      }
    };
    reader.readAsText(file, 'iso-8859-1');
  };

  const handleMappingComplete = async (mapping: Record<string, string>) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        toast({
          title: "Erreur",
          description: "Session expirée. Veuillez vous reconnecter.",
          variant: "destructive"
        });
        return;
      }

      // Vérifier les colonnes obligatoires: uniquement firstname, lastname
      const requiredFields = ['firstname', 'lastname'];
      const mappedValues = new Set(Object.values(mapping || {}));
      const missing = requiredFields.filter((f) => !mappedValues.has(f));
      if (missing.length > 0) {
        setMissingFields(missing);
        setMissingDialogOpen(true);
        return;
      }

      // Mettre à jour le mapping
      setColumnMapping(mapping);
      
      // Activer l'overlay de chargement
      setIsCheckingDuplicates(true);

      // Préparer les données pour la vérification des doublons
      // Appliquer la correspondance des professions et la profession par défaut côté front
      const professionHeader = Object.keys(mapping).find((h) => mapping[h] === 'profession');
      const professionIndex = professionHeader ? csvHeaders.indexOf(professionHeader) : -1;
      const defaultProfession = (mapping as any)['_profession_default'] as string | undefined;
      const professionMap: Record<string, string> = {};
      Object.entries(mapping).forEach(([key, value]) => {
        if (key.startsWith('_profession_')) {
          const csvValue = key.replace('_profession_', '');
          professionMap[csvValue] = value as string;
        }
      });

      const mappedData = csvContent.slice(1).map(row => {
        const mappedRow: Record<string, string> = {};
        Object.entries(mapping).forEach(([csvHeader, crmField]) => {
          if (csvHeader.startsWith('_profession_') || csvHeader === '_profession_default') return;
          const index = csvHeaders.indexOf(csvHeader);
          if (index !== -1) {
            mappedRow[crmField] = (row[index] || '').trim();
          }
        });
        // Résoudre la profession à partir de la valeur CSV + mapping + défaut
        const csvProfessionRaw = professionIndex !== -1 ? (row[professionIndex] || '').trim() : '';
        if (csvProfessionRaw) {
          if (professionMap[csvProfessionRaw]) {
            mappedRow['profession'] = professionMap[csvProfessionRaw];
          } else if (defaultProfession) {
            mappedRow['profession'] = defaultProfession;
          }
        } else if (defaultProfession) {
          mappedRow['profession'] = defaultProfession;
        }
        return mappedRow;
      });

      // Vérifier les doublons
      const duplicatesResult = await importApi.checkDuplicates(accessToken, mappedData, mapping);

      // Mettre à jour l'état des doublons
      setDuplicates(duplicatesResult || []);

      // Passer à l'étape suivante
      setCurrentStep(3);

      // Afficher une notification si des doublons sont trouvés
      if (duplicatesResult && duplicatesResult.length > 0) {
        toast({
          title: "Doublons détectés",
          description: `${duplicatesResult.length} doublon(s) trouvé(s). Veuillez les vérifier.`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Aucun doublon",
          description: "Aucun doublon n'a été détecté dans votre import.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des doublons:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la vérification des doublons.",
        variant: "destructive"
      });
    } finally {
      // Désactiver l'overlay de chargement
      setIsCheckingDuplicates(false);
    }
  };

  const handleDuplicatesResolved = async (resolvedDuplicates: any[]) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        toast({
          title: "Erreur",
          description: "Session expirée. Veuillez vous reconnecter.",
          variant: "destructive"
        });
        return;
      }

      // Préparer les données pour l'import (avec mapping des professions + défaut)
      const professionHeader2 = Object.keys(columnMapping).find((h) => columnMapping[h] === 'profession');
      const professionIndex2 = professionHeader2 ? csvHeaders.indexOf(professionHeader2) : -1;
      const defaultProfession2 = (columnMapping as any)['_profession_default'] as string | undefined;
      const professionMap2: Record<string, string> = {};
      Object.entries(columnMapping).forEach(([key, value]) => {
        if (key.startsWith('_profession_')) {
          const csvValue = key.replace('_profession_', '');
          professionMap2[csvValue] = value as string;
        }
      });

      const dataToImport = csvContent.slice(1)
        .map(row => {
          const mappedRow: Record<string, string> = {};
          Object.entries(columnMapping).forEach(([csvHeader, crmField]) => {
            if (csvHeader.startsWith('_profession_') || csvHeader === '_profession_default') return;
            const index = csvHeaders.indexOf(csvHeader);
            if (index !== -1) {
              mappedRow[crmField] = (row[index] || '').trim();
            }
          });
          const csvProfessionRaw = professionIndex2 !== -1 ? (row[professionIndex2] || '').trim() : '';
          if (csvProfessionRaw) {
            if (professionMap2[csvProfessionRaw]) {
              mappedRow['profession'] = professionMap2[csvProfessionRaw];
            } else if (defaultProfession2) {
              mappedRow['profession'] = defaultProfession2;
            }
          } else if (defaultProfession2) {
            mappedRow['profession'] = defaultProfession2;
          }
          return mappedRow;
        });

      // Préparer les données pour l'API
      const importData = {
        name: importName,
        mapping: columnMapping,
        data: dataToImport,
        duplicates: resolvedDuplicates
      };

      // Envoyer les données à l'API
      const response = await importApi.importContacts(accessToken, importData);

      // Mettre à jour le résumé de l'import
      const total = response.data.data.successCount;
      const duplicateCount = resolvedDuplicates.length;
      const failureCount = response.data.data.errorCount;
      
      const nextSummary = {
        totalRecords: dataToImport.length,
        successCount: total,
        failureCount,
        duplicateCount,
        emptyRowsCount,
      };
      
      flushSync(() => {
        console.log(nextSummary);
        setImportSummary(nextSummary);
      });

      // Passer à l'étape suivante
      setCurrentStep(4);

      // Afficher une notification de succès
      toast({
        title: "Import terminé",
        description: `Import réussi: ${total - failureCount} contacts importés, ${duplicateCount} doublons gérés, ${failureCount} échecs.`,
        variant: "default"
      });
    } catch (error: any) {
      console.error('Erreur lors de l\'import:', error);
      // Basculer vers l'étape de finalisation avec un récapitulatif d'erreur
      setImportSummary({
        totalRecords: csvContent.length > 1 ? (csvContent.length - 1) : 0,
        successCount: 0,
        failureCount: 0,
        duplicateCount: resolvedDuplicates.length,
        emptyRowsCount
      });
      setImportError(error?.message || '');
      setCurrentStep(4);
      toast({
        title: "Erreur d'import",
        description: error?.message || "Une erreur est survenue lors de l'import des contacts.",
        variant: "destructive"
      });
    }
  };

  const handleImportNameSubmit = (name: string) => {
    setImportName(name || generateDefaultImportName());
  };

  const handleFinishImport = () => {
    toast({
      title: "Import terminé",
      description: "Tous les contacts ont été importés avec succès.",
    });
    navigate("/");
  };

  const handleNextStep = () => {
    if (currentStep === 1 && selectedFile) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Rendu des étapes
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-medium mb-4">Importer un fichier CSV</h3>
              <FileUploader onFileSelect={handleFileSelect} />
              
              <div className="mt-6">
                <ImportNameInput 
                  onNameSubmit={handleImportNameSubmit} 
                  fileName={selectedFile?.name}
                />
              </div>
              
              {selectedFile && (
                <div className="mt-6">
                  <Button 
                    onClick={handleNextStep} 
                    className="w-full bg-primary hover:bg-opacity-90"
                  >
                    Passer à la correspondance
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            
            <div>
              <ImportTemplate />
            </div>
          </div>
        );
      case 2:
        return <ColumnMapper csvHeaders={csvHeaders} csvData={csvContent} onMappingComplete={handleMappingComplete} />;
      case 3:
        return <DuplicateChecker duplicates={duplicates} onDuplicatesResolved={handleDuplicatesResolved} />;
      case 4:
        return (
          <ImportSummary
            importName={importName}
            totalRecords={importSummary.totalRecords}
            successCount={importSummary.successCount}
            failureCount={importSummary.failureCount}
            duplicateCount={importSummary.duplicateCount}
            errorMessage={importError}
            onFinish={handleFinishImport}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container py-8">
      {isCheckingDuplicates && <LoadingOverlay />}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-primary">Importation de contacts</h1>
        {currentStep < 4 && (
          <Button onClick={() => navigate("/dashboard/import-history")} className="border-primary text-primary-foreground">
            Historique des imports
          </Button>
        )}
      </div>

      <div className="mb-10">
        <div className="relative">
          <div className="overflow-hidden h-2 mb-6 text-xs flex rounded bg-gray-200">
            <div
              style={{ width: `${(currentStep / 4) * 100}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
            ></div>
          </div>
          <div className="flex justify-between">
            <div className={`text-center ${currentStep >= 1 ? "text-primary" : "text-gray-400"}`}>
              <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full ${
                currentStep >= 1 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-400"
              }`}>
                <Upload size={20} />
              </div>
              <div className="text-sm mt-2">Sélection</div>
            </div>
            
            <div className={`text-center ${currentStep >= 2 ? "text-primary" : "text-gray-400"}`}>
              <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full ${
                currentStep >= 2 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-400"
              }`}>
                <LinkIcon size={20} />
              </div>
              <div className="text-sm mt-2">Correspondance</div>
            </div>
            
            <div className={`text-center ${currentStep >= 3 ? "text-primary" : "text-gray-400"}`}>
              <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full ${
                currentStep >= 3 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-400"
              }`}>
                <AlertTriangle size={20} />
              </div>
              <div className="text-sm mt-2">Doublons</div>
            </div>
            
            <div className={`text-center ${currentStep >= 4 ? "text-primary" : "text-gray-400"}`}>
              <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full ${
                currentStep >= 4 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-400"
              }`}>
                <CheckCircle size={20} />
              </div>
              <div className="text-sm mt-2">Finalisation</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
        {renderStepContent()}
      </div>

      <Dialog open={missingDialogOpen} onOpenChange={setMissingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Colonnes manquantes</DialogTitle>
            <DialogDescription>
              Les colonnes suivantes sont obligatoires pour continuer :
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 text-sm text-gray-700">
            {missingFields.length > 0 ? (
              <ul className="list-disc pl-5">
                {missingFields.map((f) => (
                  <li key={f}>
                    {f === 'firstname' ? 'Prénom' : f === 'lastname' ? 'Nom' : f === 'profession' ? 'Profession' : f}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <DialogFooter>
            <Button onClick={() => setMissingDialogOpen(false)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {currentStep < 4 && currentStep > 1 && (
        <div className="flex justify-between mt-6">
          <Button onClick={handlePrevStep} className="border-primary text-primary-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Précédent
          </Button>
        </div>
      )}
    </div>
  );
};

export default CSVImportPage;
